import type { Palette, PaletteColor } from '@mui/material/styles';
import { PaletteOptions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

/** WDUI 2.0 reference ramp stops (50–950), distinct from MUI `Color` (no A100…). */
export type RampStop =
  | 50
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 950;

export type Ramp = Record<RampStop, string>;

export type SpectrumPalette = {
  '00Periwinkle': string;
  '01SoftPurple': string;
  '02RoseViolet': string;
  '03WarmRed': string;
  '04CoralOrange': string;
  '05BrightOrange': string;
  '06SunnyGold': string;
  '07GoldenYellow': string;
  '08OliveYellow': string;
  '09FreshGreen': string;
  '10AquaGreen': string;
  '11ClearBlue': string;
};

/** Brand ramp plus semantic slots (ENG-6249 + ENG-6267). */
export type BrandPalette = Ramp & Pick<PaletteColor, 'main' | 'contrastText'>;

/** Switch row; `main` only (same pattern as nested `button.*`). */
export type SwitchPalette = {
  on: Pick<PaletteColor, 'main'>;
  off: Pick<PaletteColor, 'main'>;
  disabled: Pick<PaletteColor, 'main'>;
  thumb: Pick<PaletteColor, 'main'>;
};

export type TabPaletteTokens = {
  /** Unselected tab surface. */
  inactive: string;
  /** Hover surface for unselected tabs. */
  inactiveHover: string;
  /** Vertical divider between inactive tabs (one warmGrey step darker than `inactive`). */
  divider: string;
  /** Bottom edge on selected tab (`brand[500]`). */
  selectedIndicator: string;
};

/** Palette shape for watchduty theme component overrides (WDUI). */
export type WatchdutyThemePalette = Palette & {
  brand: BrandPalette;
  warmGrey: Ramp;
  switch: SwitchPalette;
  tab: TabPaletteTokens;
};

/** ENG-6249 reference palettes (same values in light and dark). */
const referenceColorRamps = {
  warmGrey: {
    50: '#F7F6F3',
    100: '#F0EFEB',
    200: '#E3E1DC',
    300: '#D4D1CB',
    400: '#C2BEB7',
    500: '#B7B2AC',
    600: '#A8A19B',
    700: '#857D77',
    800: '#625B5B',
    900: '#4A4444',
    950: '#2F2A2A',
  },
  brand: {
    50: '#FFF9EC',
    100: '#FFF1D1',
    200: '#FDE3A3',
    300: '#FCD06A',
    400: '#FBBE3F',
    500: '#FBB92D',
    600: '#E0A526',
    700: '#C78F20',
    800: '#A8761A',
    900: '#855C14',
    950: '#5C3F0E',
  },
  khaki: {
    50: '#FEF8F0',
    100: '#FDF1E2',
    200: '#FBE7CD',
    300: '#EFD8C0',
    400: '#E0CAB1',
    500: '#CDB69E',
    600: '#AC9B85',
    700: '#8F7E69',
    800: '#726353',
    900: '#55493E',
    950: '#3A312A',
  },
  olive: {
    50: '#F6F5EF',
    100: '#ECEADE',
    200: '#D8D3BC',
    300: '#C1BA96',
    400: '#A39A6E',
    500: '#7B7346',
    600: '#544F2B',
    700: '#474224',
    800: '#3A361D',
    900: '#2C2916',
    950: '#1E1C10',
  },
  deepRed: {
    50: '#F9ECEC',
    100: '#F2D4D4',
    200: '#E5A8A8',
    300: '#D67A7A',
    400: '#C44D4D',
    500: '#A83232',
    600: '#872424',
    700: '#6A1A1A',
    800: '#4A0F0F',
    900: '#310000',
    950: '#1E0000',
  },
  spectrum: {
    '00Periwinkle': '#8C90F0',
    '01SoftPurple': '#B28AF3',
    '02RoseViolet': '#CC78B3',
    '03WarmRed': '#DB6B75',
    '04CoralOrange': '#EC8A61',
    '05BrightOrange': '#F8A63F',
    '06SunnyGold': '#F3C842',
    '07GoldenYellow': '#EADA53',
    '08OliveYellow': '#BFD363',
    '09FreshGreen': '#7AD56C',
    '10AquaGreen': '#5BC7A4',
    '11ClearBlue': '#5DB8E8',
  },
} satisfies {
  warmGrey: Ramp;
  brand: Ramp;
  khaki: Ramp;
  olive: Ramp;
  deepRed: Ramp;
  spectrum: SpectrumPalette;
};

const warmGreyRamp = referenceColorRamps.warmGrey;
const brandRamp = referenceColorRamps.brand;
const khakiRamp = referenceColorRamps.khaki;
const deepRedRamp = referenceColorRamps.deepRed;

export const WHITE = '#FFFFFF';
export const DARK_SURFACE = '#1B1818';
const INFO_MAIN = '#13A1EC';
const MODERATE_MAIN = '#BD3D3D';

const alertLight = {
  major: {
    main: '#7653BC',
    secondary: '#8A6CC6',
    background: '#F0EDF8',
    contrastText: '#FFFFFF',
  },
  moderate: {
    main: MODERATE_MAIN,
    secondary: '#CD6565',
    background: '#F9ECEC',
  },
  minor: {
    main: '#EC7826',
    secondary: '#EF8A43',
    background: '#FDF0E8',
  },
  action: {
    main: brandRamp[500],
    secondary: brandRamp[300],
    background: brandRamp[100],
    contrastText: warmGreyRamp[950],
  },
  normal: {
    main: '#4EB14E',
    secondary: '#71C171',
    background: '#EDF7ED',
    contrastText: warmGreyRamp[950],
  },
  info: {
    main: INFO_MAIN,
    secondary: '#42B4F0',
    background: '#E7F6FD',
  },
  neutral: {
    main: warmGreyRamp[500],
    secondary: warmGreyRamp[400],
    background: warmGreyRamp[100],
    contrastText: warmGreyRamp[950],
    border: warmGreyRamp[400],
  },
} as const;

const alertDark = {
  major: {
    main: '#7653BC',
    secondary: '#573993',
    background: '#140E25',
    contrastText: '#FFFFFF',
  },
  moderate: {
    main: MODERATE_MAIN,
    secondary: '#9A3232',
    background: '#270C0C',
  },
  minor: {
    main: '#EC7826',
    secondary: '#BC5710',
    background: '#2F1404',
  },
  action: {
    main: brandRamp[500],
    secondary: brandRamp[700],
    background: '#2C1E07',
    contrastText: warmGreyRamp[950],
  },
  normal: {
    main: '#4EB14E',
    secondary: '#3E8E3E',
    background: '#102310',
    contrastText: warmGreyRamp[950],
  },
  info: {
    main: INFO_MAIN,
    secondary: '#0F81BD',
    background: '#04212F',
  },
  neutral: {
    main: warmGreyRamp[700],
    secondary: warmGreyRamp[800],
    background: warmGreyRamp[900],
    contrastText: '#2A2A2A',
    border: warmGreyRamp[700],
  },
} as const;

/** Switch palette row — not tied to alert severity semantics. */
const switchLight: SwitchPalette = {
  on: { main: alertLight.normal.main },
  off: { main: alertLight.neutral.main },
  disabled: { main: alertLight.neutral.background },
  thumb: { main: WHITE },
};

const switchDark: SwitchPalette = {
  on: { main: alertDark.normal.main },
  off: { main: alertDark.neutral.main },
  disabled: { main: alertDark.neutral.background },
  thumb: { main: alertDark.neutral.contrastText },
};

const brandLight: BrandPalette = {
  ...brandRamp,
  main: brandRamp[500],
  contrastText: warmGreyRamp[950],
};

const brandDark: BrandPalette = {
  ...brandRamp,
  main: '#2C1E07',
  contrastText: brandRamp[500],
};

export const palette: PaletteOptions = {
  common: {
    black: warmGreyRamp[950],
    white: WHITE,
  },
  primary: {
    main: warmGreyRamp[950],
    contrastText: brandRamp[500],
  },
  secondary: {
    main: warmGreyRamp[600],
    contrastText: warmGreyRamp[950],
  },
  // `light` / `dark` are required so MUI can build `palette.Alert.*` tokens for standard/filled Alerts.
  info: {
    main: INFO_MAIN,
    light: alertLight.info.background,
    dark: alertDark.info.secondary,
  },
  error: {
    main: MODERATE_MAIN,
    light: alertLight.moderate.background,
    dark: alertDark.moderate.secondary,
  },
  success: {
    main: alertLight.normal.main,
    light: alertLight.normal.background,
    dark: alertDark.normal.secondary,
  },
  warning: {
    light: '#FDF0E8',
    main: '#EC7826',
    dark: '#EF8A43',
  },
  accent: {
    main: INFO_MAIN,
  },
  severe: {
    main: '#7653BC',
  },
  divider: alpha(warmGreyRamp[950], 0.12),
  outline: alpha(warmGreyRamp[950], 0.26),
  action: {
    active: alpha(warmGreyRamp[950], 0.54),
    hover: alpha(warmGreyRamp[950], 0.04),
    focus: alpha(warmGreyRamp[950], 0.12),
    disabled: alpha(warmGreyRamp[950], 0.26),
  },
  text: {
    primary: warmGreyRamp[950],
    secondary: alpha(warmGreyRamp[950], 0.7),
    disabled: alpha(warmGreyRamp[950], 0.3),
    link: MODERATE_MAIN,
  },
  background: {
    default: WHITE,
    secondary: warmGreyRamp[50],
    tertiary: warmGreyRamp[600],
    hover: warmGreyRamp[200],
    paper: WHITE,
    membership: khakiRamp[100],
    inlineInfoCard: '#FDF0E8',
    backdrop: alpha(warmGreyRamp[950], 0.4),
    welcomeInfoCard: 'rgba(255,255,255,0.8)',
    headerAppBar: brandRamp[500],
  },
  fire: {
    main: '#E25345',
    contrastText: warmGreyRamp[950],
  },
  flood: {
    main: '#1257DD',
    contrastText: WHITE,
  },
  watchdutyComplex: {
    main: '#F19437',
    contrastText: warmGreyRamp[950],
  },
  button: {
    contrast: {
      main: brandRamp[500],
      dark: warmGreyRamp[950],
      light: warmGreyRamp[900],
    },
    neutral: {
      main: warmGreyRamp[950],
      dark: warmGreyRamp[200],
      light: warmGreyRamp[300],
    },
    surface: {
      main: warmGreyRamp[950],
      dark: WHITE,
      light: grey[500],
    },
    error: {
      main: deepRedRamp[700],
      dark: deepRedRamp[50],
      light: deepRedRamp[100],
    },
    success: {
      main: warmGreyRamp[950],
      dark: alertLight.normal.main,
      light: alertDark.normal.secondary,
    },
  },
  tab: {
    inactive: warmGreyRamp[100],
    inactiveHover: warmGreyRamp[300],
    divider: warmGreyRamp[300],
    selectedIndicator: brandRamp[500],
  },
  switch: {
    ...switchLight,
  },
  alert: {
    ...alertLight,
  },
  ...referenceColorRamps,
  brand: brandLight,
  chart: [
    WHITE,
    warmGreyRamp[50],
    warmGreyRamp[100],
    warmGreyRamp[500],
    warmGreyRamp[700],
  ] as const,
};

export const darkPalette: PaletteOptions = {
  ...palette,
  mode: 'dark',
  common: {
    black: WHITE,
    white: warmGreyRamp[950],
  },
  primary: {
    main: brandRamp[500],
    contrastText: warmGreyRamp[950],
  },
  secondary: {
    main: warmGreyRamp[400],
    contrastText: warmGreyRamp[950],
  },
  // Dark surfaces: use light ink on dark backgrounds. (Token `common.white` is warmGrey[950] per
  // ENG-6264 swap — that is a surface tone, not readable body text on dark default/paper.)
  divider: alpha(WHITE, 0.12),
  outline: alpha(WHITE, 0.26),
  action: {
    active: alpha(WHITE, 0.54),
    hover: alpha(WHITE, 0.04),
    focus: alpha(WHITE, 0.12),
    disabled: alpha(WHITE, 0.26),
  },
  text: {
    primary: WHITE,
    secondary: alpha(WHITE, 0.7),
    disabled: alpha(WHITE, 0.3),
    link: MODERATE_MAIN,
  },
  // Surface hierarchy (WDUI dark): default / paper = page canvas (#2C2520); headerAppBar = chrome;
  // secondary = inset rows on canvas. Full-page bodies use getPageBackground().
  background: {
    ...palette.background,
    default: DARK_SURFACE,
    secondary: warmGreyRamp[950],
    tertiary: warmGreyRamp[800],
    hover: warmGreyRamp[900],
    paper: DARK_SURFACE,
    membership: khakiRamp[900],
    inlineInfoCard: '#2F1404',
    backdrop: 'rgba(0,0,0,0.4)',
    welcomeInfoCard: 'rgba(0,0,0,0.8)',
    headerAppBar: '#2C1E07',
  },
  brand: brandDark,
  fire: {
    main: '#E25345',
    contrastText: warmGreyRamp[950],
  },
  flood: {
    main: '#1257DD',
    contrastText: WHITE,
  },
  watchdutyComplex: {
    main: '#F19437',
    contrastText: WHITE,
  },
  severe: {
    main: '#7653BC',
  },
  info: {
    main: INFO_MAIN,
    light: alertDark.info.secondary,
    dark: alertDark.info.background,
  },
  error: {
    main: MODERATE_MAIN,
    light: alertDark.moderate.secondary,
    dark: alertDark.moderate.background,
  },
  success: {
    main: alertDark.normal.main,
    light: alertDark.normal.secondary,
    dark: alertDark.normal.background,
  },
  button: {
    contrast: {
      main: brandRamp[500],
      dark: grey[700],
      light: grey[800],
    },
    neutral: {
      main: WHITE,
      dark: warmGreyRamp[800],
      light: warmGreyRamp[700],
    },
    surface: {
      main: WHITE,
      // Map floating controls (Layers, Legend, etc.); `background.tertiary` is for footer bars.
      dark: warmGreyRamp[900],
      light: warmGreyRamp[800],
    },
    error: {
      main: WHITE,
      dark: deepRedRamp[700],
      light: deepRedRamp[800],
    },
    success: {
      main: WHITE,
      dark: alertLight.normal.main,
      light: alertDark.normal.secondary,
    },
  },
  tab: {
    inactive: '#0D0C0C',
    inactiveHover: warmGreyRamp[800],
    divider: warmGreyRamp[900],
    selectedIndicator: brandRamp[500],
  },
  switch: {
    ...switchDark,
  },
  alert: {
    ...alertDark,
  },
  chart: [
    warmGreyRamp[950],
    warmGreyRamp[900],
    warmGreyRamp[500],
    warmGreyRamp[500],
    warmGreyRamp[300],
  ] as const,
};
